import { Tracing } from "@tento-core/tracing";
import { CalendarSubcolumn } from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import type { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import type { SheetViewModel, SheetRow } from "@tento-lona";
import { EventItemAllDay, type Option } from "@tento-lona/sheets";
import type { TimelineContentProvider } from "@tento-lona/sheets-ui/plugins/layout/timeline-shared/timeline-content-provider.ts";
import { DefaultBinderHelpers } from "@tento-lona/sheets-ui/sheet/binder/default-binder-helpers.ts";
import type { ShapeKey } from "@tento-lona/sheets";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";
import type { SheetBinder } from "../../../sheet/sheet-index-renderer.ts";

type PluginManagerLike = SheetDelegate.PluginManager;

type CalendarProviderPlugin = {
  contentProviders?: Option<{
    calendar?: CalendarSubcolumn.Provider;
  }>;
  makeTimelineProvider?: (
    row: SheetRow.Joined.Handle,
  ) => TimelineContentProvider.Provider<EventItemAllDay>;
};

function pluginAcceptsShape(
  plugin: object,
  shape: ShapeKey<unknown>,
): boolean {
  const spec =
    (plugin as { spec?: PerTypeSpec }).spec ??
    (plugin.constructor as { spec?: PerTypeSpec }).spec;
  // Convention from `checkRenderPluginShape`: a plugin without an
  // accepts list (e.g. alias aggregators) is treated as accepting
  // any shape. Only plugins that explicitly declare accepts AND
  // omit the requested shape get filtered out.
  if (!spec?.accepts || spec.accepts.length === 0) return true;
  for (const accepted of spec.accepts) {
    if (accepted.shape.id === shape.id) return true;
  }
  return false;
}

function collectRows(
  row: SheetRow.Joined.Handle,
  visit: (child: SheetRow.Joined.Handle) => boolean,
): void {
  for (const child of row.children()) {
    // Keep nested calendar aliases isolated; the current calendar's
    // providers live below its synthesized layout row.
    if (child.descriptor.id === "calendar" || child.descriptor.id === "alias.calendar") continue;
    const shouldDescend = visit(child);
    if (!shouldDescend) continue;
    collectRows(child, visit);
  }
}

function calendarProvider(
  pluginManager: PluginManagerLike,
  row: SheetRow.Joined.Handle,
  shape: Option<ShapeKey<unknown>>,
): Option<{ provider: CalendarSubcolumn.Provider; pluginId: string }> {
  const plugins = DefaultBinderHelpers.resolveRowCapabilityPlugins(row, pluginManager);
  for (const plugin of plugins) {
    if (shape && !pluginAcceptsShape(plugin, shape)) continue;
    const provider = (plugin as CalendarProviderPlugin).contentProviders
      ?.calendar;
    if (provider) {
      return {
        provider,
        pluginId: (plugin as { id: string }).id,
      };
    }
  }
  return null;
}

/** Last-logged provider list per layout row — discovery runs per
 *  column bind (dozens/sec during initial paint), so the trace only
 *  records CHANGES to a row's provider set. */
type ProviderDiagnostic = { shapeId: Option<string>; providers: string };
// SDK joined facades are interned per sheet/row. Neither weak key extends
// a sheet or row lifetime; each live row retains only its latest query.
const lastLoggedProviders = new WeakMap<
  SheetViewModel.Surface<SheetBinder>,
  WeakMap<SheetRow.Joined.Handle, ProviderDiagnostic>
>();

function collectCalendarSubcolumnsInternal(
  sheet: SheetViewModel.Surface<SheetBinder>,
  row: SheetRow.Joined.Handle,
  pluginManager: PluginManagerLike,
  shape: Option<ShapeKey<unknown>>,
): CalendarSubcolumn[] {
  const subcolumns: CalendarSubcolumn[] = [];
  collectRows(row, (child) => {
    // Boundary: a row whose plugin resolution or subcolumn
    // construction throws must not kill the whole column bind —
    // every other subcolumn (events, tasks) would die with it and
    // the calendar would render inert. Warn with context and skip.
    try {
      const resolved = calendarProvider(pluginManager, child, shape);
      const subcolumn = resolved?.provider.subcolumnFromRow(
        sheet,
        child,
        row,
        pluginManager,
      ) as Option<CalendarSubcolumn>;
      if (!subcolumn || resolved == null) return true;
      subcolumns.push(
        subcolumn.providerId != null
          ? subcolumn
          : { ...subcolumn, providerId: resolved.pluginId },
      );
      return false;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      Tracing.warn("ui")(
        `calendar-subcolumn discovery: skipping row ${String(child.id)} ` +
          `(${child.descriptor.id}) — ${msg}`,
      );
      // Session-log visibility: a skipped provider silently changes
      // create-handler priority for every drag until the next rebind
      // (observed as "first drag opens the wrong create form").
      Tracing.log("calendar-subcolumn/skip", {
        rowId: String(child.id),
        descriptor: child.descriptor.id,
        error: msg,
      });
      return true;
    }
  });
  const rowKey = String(row.id);
  const providers = subcolumns.map((s) => s.providerId ?? "?").join(",");
  let byRow = lastLoggedProviders.get(sheet);
  if (!byRow) {
    byRow = new WeakMap();
    lastLoggedProviders.set(sheet, byRow);
  }
  const shapeId = shape?.id ?? null;
  const previous = byRow.get(row);
  if (previous?.providers !== providers || previous?.shapeId !== shapeId) {
    byRow.set(row, { providers, shapeId });
    Tracing.log("calendar-subcolumn/discovered", { rowId: rowKey, providers, shapeId });
  }
  return subcolumns;
}

export function collectCalendarSubcolumns(
  sheet: SheetViewModel.Surface<SheetBinder>,
  row: SheetRow.Joined.Handle,
  pluginManager: PluginManagerLike,
): CalendarSubcolumn[] {
  return collectCalendarSubcolumnsInternal(sheet, row, pluginManager, null);
}

export function collectCalendarSubcolumnsForShape(
  sheet: SheetViewModel.Surface<SheetBinder>,
  row: SheetRow.Joined.Handle,
  pluginManager: PluginManagerLike,
  shape: ShapeKey<unknown>,
): CalendarSubcolumn[] {
  return collectCalendarSubcolumnsInternal(
    sheet,
    row,
    pluginManager,
    shape,
  );
}

export function findTimelineProviderUnderCalendarRow(
  row: SheetRow.Joined.Handle,
  pluginManager: PluginManagerLike,
): Option<TimelineContentProvider.Provider<EventItemAllDay>> {
  let found: Option<TimelineContentProvider.Provider<EventItemAllDay>> = null;
  collectRows(row, (child) => {
    if (found) return false;
    for (const plugin of DefaultBinderHelpers.resolveRowCapabilityPlugins(child, pluginManager)) {
      const provider = (plugin as CalendarProviderPlugin).makeTimelineProvider
        ?.(child);
      if (!provider) continue;
      found = provider;
      return false;
    }
    return true;
  });
  return found;
}

/**
 * Resolve a calendar timeline provider for a `timeline[row].event`
 * renderer row. The renderer runs on the `events` list row, whose
 * `calendar-list` parent owns `makeTimelineProvider`; the
 * descendants-only `findTimelineProviderUnderCalendarRow` therefore
 * misses it. Try the down-walk first (covers the legacy case where the
 * renderer sits on the strip row directly), then climb ancestors and
 * return the first calendar-list provider found.
 */
export function findTimelineProviderForRendererRow(
  row: SheetRow.Joined.Handle,
  pluginManager: PluginManagerLike,
): Option<TimelineContentProvider.Provider<EventItemAllDay>> {
  const direct = findTimelineProviderUnderCalendarRow(row, pluginManager);
  if (direct) return direct;

  for (let ancestor = row.parent(); ancestor; ancestor = ancestor.parent()) {
    for (const plugin of DefaultBinderHelpers.resolveRowCapabilityPlugins(
      ancestor,
      pluginManager,
    )) {
      const provider = (plugin as CalendarProviderPlugin).makeTimelineProvider?.(
        ancestor,
      );
      if (provider) return provider;
    }
  }
  return null;
}
